/**
 * Cognara reads the uploaded PO and proposes the commercial header. Nothing is
 * written until each field is accepted — a silently overwritten contract value
 * is worse than a blank one.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Loader2, Check } from 'lucide-react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { fmtINR, apiError } from './portfolioUi';

const FIELDS = [
  ['supplier', 'Supplier'],
  ['contract_value', 'Contract value'],
  ['currency', 'Currency'],
  ['start_date', 'Start date'],
  ['completion_date', 'Completion date'],
  ['payment_terms', 'Payment terms'],
  ['retention_percent', 'Retention %'],
];

export const HeaderExtractCard = ({ contractId, onApplied }) => {
  const [result, setResult] = useState(null);
  const [edits, setEdits] = useState({});
  const [accepted, setAccepted] = useState({});
  const [moveVendor, setMoveVendor] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/contracts/${contractId}/extract-header`);
      setResult(data);
      setEdits({ ...data.proposed });
      setAccepted(Object.fromEntries(FIELDS
        .filter(([k]) => data.proposed[k] !== undefined && data.proposed[k] !== null)
        .map(([k]) => [k, true])));
    } catch (e) {
      toast.error(apiError(e, 'Cognara could not read a header from these documents'));
    } finally { setBusy(false); }
  };

  const apply = async () => {
    const fields = Object.fromEntries(Object.entries(edits)
      .filter(([k]) => accepted[k] && FIELDS.some(([f]) => f === k)));
    if (!Object.keys(fields).length) { toast.error('Accept at least one field'); return; }
    setBusy(true);
    try {
      await api.post(`/contracts/contracts/${contractId}/apply-header`, { fields, move_vendor: moveVendor });
      toast.success('Contract header updated from the purchase order');
      setResult(null);
      onApplied?.();
    } catch (e) {
      toast.error(apiError(e, 'Could not apply the header'));
    } finally { setBusy(false); }
  };

  if (!result) {
    return (
      <button onClick={run} disabled={busy}
              className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="extract-header-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        {busy ? 'Cognara is reading the PO…' : 'Extract supplier · value · dates from documents'}
      </button>
    );
  }

  const conf = result.proposed.confidence || {};
  const evid = result.proposed.evidence || {};

  return (
    <div className="surface-card rounded-xl p-5" data-testid="extract-header-result">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h4 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Cognara read the purchase order</h4>
          <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
            From {result.documents.join(', ')} — accept or edit each field, nothing is written until you apply.
          </p>
        </div>
        <button onClick={() => setResult(null)} className="text-[11px]" style={{ color: 'var(--text-muted)' }}
                data-testid="extract-header-discard">Discard</button>
      </div>

      <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
        {FIELDS.map(([key, label]) => {
          const proposed = result.proposed[key];
          if (proposed === undefined || proposed === null || proposed === '') return null;
          const current = result.current?.[key];
          return (
            <div key={key} className="py-3 grid grid-cols-[1fr_auto] gap-4 items-center" data-testid={`extract-field-${key}`}>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
                  {label}
                  {conf[key] != null && <span className="ml-2 font-normal">{Math.round(conf[key] * 100)}% confident</span>}
                </p>
                <Input value={edits[key] ?? ''} onChange={(e) => setEdits(s => ({ ...s, [key]: e.target.value }))}
                       className="h-8 text-sm mt-1.5" data-testid={`extract-input-${key}`} />
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  currently {key === 'contract_value' ? (current ? fmtINR(current) : 'not set') : (current || 'not set')}
                  {evid[key] && ` · “${String(evid[key]).slice(0, 110)}”`}
                </p>
              </div>
              <label className="flex items-center gap-2 text-[11px] shrink-0" style={{ color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={!!accepted[key]}
                       onChange={(e) => setAccepted(s => ({ ...s, [key]: e.target.checked }))}
                       data-testid={`extract-accept-${key}`} />
                accept
              </label>
            </div>
          );
        })}
      </div>

      {result.proposed.supplier && (
        <label className="flex items-center gap-2 text-xs mt-4" style={{ color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={moveVendor} onChange={(e) => setMoveVendor(e.target.checked)}
                 data-testid="extract-move-vendor" />
          Also move this contract under “{edits.supplier || result.proposed.supplier}” in the vendor hierarchy
        </label>
      )}

      <button onClick={apply} disabled={busy}
              className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm text-white"
              style={{ background: 'var(--brand-accent)' }} data-testid="extract-apply">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Apply to contract
      </button>
    </div>
  );
};

export default HeaderExtractCard;
